import { useState } from "react";
import { Mail, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import logger from "../lib/logger";

/**
 * Inline email opt-in band shown on the home page below the intro section.
 * Submits to the same /api/subscribe endpoint as the email lightbox, so the
 * collected addresses land in the same admin-visible list.
 */
export default function InlineEmailSignup() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await api.subscribe(email.trim());
      setAlreadySubscribed(!!res.already_subscribed);
      setSubmitted(true);
    } catch (err) {
      logger.warn("Inline subscribe failed:", err.message);
      setError(
        err?.response?.data?.detail ||
          "Could not subscribe right now. Please try again later."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="py-16 px-6 md:px-12"
      data-testid="inline-email-signup-section"
    >
      <div className="max-w-3xl mx-auto">
        <div className="bg-demolition-paper border border-demolition-red/40 p-8 md:p-10">
          {submitted ? (
            <div className="text-center py-4" data-testid="inline-email-signup-success">
              <CheckCircle2 className="w-12 h-12 text-demolition-red mx-auto mb-3" />
              <h3 className="font-russo text-2xl sm:text-3xl text-white uppercase tracking-tighter mb-2">
                {alreadySubscribed ? "ALREADY ON THE LIST" : "YOU'RE IN"}
              </h3>
              <p className="font-mono text-white/60 text-sm">
                {alreadySubscribed
                  ? "Looks like you've already subscribed. Stay tuned for the next drop."
                  : "Watch your inbox for the next DemoPod episode and merch drop."}
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <Mail className="w-7 h-7 text-demolition-red flex-shrink-0" />
                <h3 className="font-russo text-2xl sm:text-3xl text-white uppercase tracking-tighter">
                  STAY IN THE LOOP
                </h3>
              </div>
              <p className="font-mono text-white/70 text-sm mb-6 leading-relaxed">
                Get notified about new DemoPod episodes and merch drops. No spam, ever.
              </p>

              <form
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3"
                data-testid="inline-email-signup-form"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 px-4 py-3 bg-demolition-subtle border border-white/20 text-white font-mono placeholder:text-white/30 focus:outline-none focus:border-demolition-red transition-colors"
                  data-testid="inline-email-signup-input"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-8 py-3 bg-demolition-red text-white font-teko text-lg uppercase tracking-widest hover:bg-demolition-red-hover transition-colors disabled:opacity-50 flex-shrink-0"
                  data-testid="inline-email-signup-submit"
                >
                  {submitting ? "SUBSCRIBING..." : "SUBSCRIBE"}
                </button>
              </form>

              {error && (
                <p
                  className="font-mono text-demolition-red text-xs mt-3"
                  data-testid="inline-email-signup-error"
                >
                  {error}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
